import React, { useEffect, useState } from 'react';
import { Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const C = {
  bg: '#FAFAF9', surface: '#FFFFFF', surface2: '#F6F6F4',
  ink1: '#0B1020', ink2: '#1F2937', ink3: '#475569', ink4: '#64748B', ink5: '#94A3B8',
  line: '#E7E5E4', line2: '#EFEDEA',
  indigo: '#4F46E5', indigo50: '#EEF2FF',
  emerald: '#059669', emerald50: '#ECFDF5',
  rose: '#E11D48', rose50: '#FFF1F2',
  shadowSm: '0 1px 2px rgba(15,23,42,.04)',
};

const f = {
  display: "'Inter Tight','Inter',sans-serif",
  body: "'Inter',system-ui,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,monospace",
};

function isValidIPv4(ip) {
  return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
}

function parseDbDate(str) {
  if (!str) return null;
  return new Date(str.replace(' ', 'T'));
}

function Btn({ primary, danger, onClick, type, disabled, children }) {
  const bg = primary ? C.indigo : danger ? C.rose : C.surface;
  const border = primary ? C.indigo : danger ? C.rose : C.line;
  const color = primary || danger ? '#fff' : C.ink2;
  return (
    <button type={type || 'button'} onClick={onClick} disabled={disabled} style={{
      height: 36, padding: '0 14px', borderRadius: 7, border: `1px solid ${border}`,
      background: disabled ? C.surface2 : bg, color: disabled ? C.ink5 : color,
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12,
      fontFamily: f.body, fontWeight: 500, transition: '.12s',
    }}>{children}</button>
  );
}

function TypePill({ type }) {
  const isAllow = type === 'allow';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 999, fontSize: 10.5, fontWeight: 500, fontFamily: f.body,
      background: isAllow ? C.emerald50 : C.rose50,
      color: isAllow ? C.emerald : C.rose,
      border: `1px solid ${isAllow ? '#A7F3D0' : '#FECDD3'}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {isAllow ? 'Allow' : 'Deny'}
    </span>
  );
}

function GlobalIpRules({ apiBase, token }) {
  const [rules, setRules] = useState([]);
  const [ip, setIp] = useState('');
  const [ruleType, setRuleType] = useState('allow');
  const [detail, setDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ipError, setIpError] = useState('');

  const fetchRules = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/global-ip-rules`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to fetch rules');
      setRules(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!apiBase || !token) return;
    fetchRules();
  }, [apiBase, token]); // eslint-disable-line

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidIPv4(ip)) { setIpError('Invalid IPv4 address'); return; }
    setIpError('');
    try {
      const res = await fetch(`${apiBase}/global-ip-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ ip, ruleType, detail }),
      });
      if (!res.ok) throw new Error('Failed to add rule');
      setIp(''); setRuleType('allow'); setDetail('');
      fetchRules();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/global-ip-rules/${deleteId}`, {
        method: 'DELETE', credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to delete rule');
      setDeleteId(null);
      fetchRules();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <style>{`@keyframes lm-spin{to{transform:rotate(360deg)}} .lm-ip tr:hover td{background:#FDFCFB}`}</style>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '18px 20px 60px', background: C.bg, minHeight: '100%' }}>

        {/* page header */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, color: C.ink5, marginBottom: 3 }}>
            License Manager &nbsp;/&nbsp; <b style={{ color: C.ink3, fontWeight: 500 }}>Global IP Rules</b>
          </div>
          <h1 style={{ fontFamily: f.display, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: C.ink1 }}>
            Global IP Rules
          </h1>
        </div>

        {/* add rule card */}
        <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, padding: '14px 16px', boxShadow: C.shadowSm, marginBottom: 12 }}>
          <div style={{ fontFamily: f.display, fontWeight: 600, fontSize: 13, color: C.ink1, marginBottom: 12 }}>Add Rule</div>
          <form onSubmit={handleAdd}>
            <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <TextField
                  label="IP Address"
                  value={ip}
                  onChange={e => { setIp(e.target.value); setIpError(''); }}
                  required size="small"
                  error={!!ipError}
                  helperText={ipError}
                  placeholder="e.g. 192.168.1.1"
                  sx={{ width: 190 }}
                />
              </div>
              {/* rule type toggle */}
              <div style={{ display: 'inline-flex', background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 7, padding: 2, gap: 2, alignSelf: 'flex-start', marginTop: 1 }}>
                {['allow', 'deny'].map(t => (
                  <button key={t} type="button" onClick={() => setRuleType(t)} style={{
                    padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 5,
                    background: ruleType === t ? C.surface : 'none',
                    color: ruleType === t ? (t === 'allow' ? C.emerald : C.rose) : C.ink4,
                    boxShadow: ruleType === t ? C.shadowSm : 'none',
                    border: 0, cursor: 'pointer', fontFamily: f.body, transition: '.12s', textTransform: 'capitalize',
                  }}>{t}</button>
                ))}
              </div>
              <TextField
                label="Detail (optional)"
                value={detail}
                onChange={e => setDetail(e.target.value)}
                size="small" sx={{ minWidth: 220 }}
              />
              <Btn primary type="submit" disabled={!isValidIPv4(ip)}>Add Rule</Btn>
            </Box>
          </form>
        </div>

        {error && (
          <div style={{ background: C.rose50, border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', color: C.rose, fontSize: 12, marginBottom: 10 }}>
            {error}
          </div>
        )}

        {/* rules table card */}
        <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: C.shadowSm, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ display: 'inline-block', width: 24, height: 24, border: `3px solid ${C.line}`, borderTopColor: C.indigo, borderRadius: '50%', animation: 'lm-spin .9s linear infinite' }} />
            </div>
          ) : (
            <table className="lm-ip" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['ID', 'IP Address', 'Type', 'Detail', 'Created At', ''].map((h, i) => (
                    <th key={i} style={{
                      textAlign: i === 5 ? 'right' : 'left', fontSize: 10.5, color: C.ink4, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '.05em', padding: '9px 14px',
                      borderBottom: `1px solid ${C.line}`, background: '#FCFCFB', fontFamily: f.body,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(rules || []).length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: C.ink5, fontSize: 12 }}>No rules defined.</td></tr>
                ) : (rules || []).map(rule => (
                  <tr key={rule.id}>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}`, fontFamily: f.mono, fontSize: 11, color: C.ink4 }}>#{rule.id}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}`, fontFamily: f.mono, fontSize: 12, fontWeight: 600, color: C.ink1 }}>{rule.ip}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}` }}><TypePill type={rule.ruleType} /></td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}`, fontSize: 12, color: C.ink3 }}>{rule.detail || '—'}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}`, fontFamily: f.mono, fontSize: 11, color: C.ink4 }}>
                      {(() => { const d = parseDbDate(rule.createdAt); return d && d.getTime() > 0 ? d.toLocaleString('tr-TR') : '—'; })()}
                    </td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.line2}`, textAlign: 'right' }}>
                      <button onClick={() => setDeleteId(rule.id)} style={{
                        height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid #FECDD3`,
                        background: C.rose50, color: C.rose, cursor: 'pointer', fontSize: 11.5,
                        fontFamily: f.body, fontWeight: 500,
                      }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* delete confirm dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontFamily: f.display, fontWeight: 600, fontSize: 15 }}>Delete Rule</DialogTitle>
        <DialogContent sx={{ fontSize: 13, color: C.ink3 }}>Are you sure you want to delete this rule?</DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Btn onClick={() => setDeleteId(null)} disabled={deleteLoading}>Cancel</Btn>
          <Btn danger onClick={handleDelete} disabled={deleteLoading}>{deleteLoading ? 'Deleting…' : 'Delete'}</Btn>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GlobalIpRules;
